import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import "./styles-drawer.css";
import { Tooltip } from '../smartTable/Tooltip';
import { useDrawerLayer } from './drawerLayerStack';

/** Ancho del rail en `xl+` (debe coincidir con `w-16` / `4rem`). */
const MENU_RAIL_WIDTH = '4rem';
const TW_XL_MIN = 1280;

/** Cuando el menú está oculto solo en `xl+`, el panel del drawer se estrecha para no dejar franja vacía. */
function narrowDrawerSizeForHiddenMenu(size, shouldNarrow) {
    if (!shouldNarrow) return size;
    if (typeof size !== 'string') return size;
    const t = size.trim();
    if (/^\d+(\.\d+)?%$/.test(t)) {
        return `calc(${t} - ${MENU_RAIL_WIDTH})`;
    }
    return size;
}

/** Duración y curva compartidas: panel del drawer (width) + columna del menú (w-16 ↔ w-0). */
const MENU_RAIL_ANIM_MS = 640;
const MENU_RAIL_ANIM_EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';

const menuGutterWidthMotion =
    'xl:transition-[width] xl:duration-[640ms] xl:ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:xl:transition-none motion-reduce:xl:duration-0';

export const DrawerNoHeader = ({
    isOpen,
    toggleDrawer,
    children,
    direction = 'right',
    sizeSM = '100%',
    sizeMD = '70%',
    sizeLG = '65%',
    sizeXL = '50%',
    sizeXXL = '40%',
    footer,
    externalSideContent = null,
    externalSideJoined = false,
    menuBar = null,
    menuExpand = null,
    panelClassName = '',
    /** Si es true, overlay/Escape no cierran el drawer (p. ej. mientras guarda). */
    lockClose = false,
}) => {
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);

        // Cleanup event listener on component unmount
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handleClose = useCallback(() => {
        if (lockClose) return;
        toggleDrawer();
    }, [lockClose, toggleDrawer]);

    const { zIndex } = useDrawerLayer({
        isOpen,
        onRequestClose: handleClose,
        lockClose,
    });

    /** Menú lateral + panel principal como una sola pieza (un borde, una sombra, sin separación). */
    const useUnifiedShell = Boolean(menuBar?.enabled || externalSideJoined);

    /** Si `menuBar.visible` es false, el rail se oculta con animación (p. ej. al cambiar de pestaña). Por defecto visible cuando `enabled`. */
    const menuBarRailVisible = menuBar?.enabled && menuBar?.visible !== false;

    const getResponsiveSize = (windowWidth) => {
        const effectiveSizeLG = menuExpand?.enabled && menuExpand?.isExpanded ? (menuExpand?.sizeLG || sizeLG) : sizeLG;
        const effectiveSizeXL = menuExpand?.enabled && menuExpand?.isExpanded ? (menuExpand?.sizeXL || sizeXL) : sizeXL;
        const effectiveSizeXXL = menuExpand?.enabled && menuExpand?.isExpanded ? (menuExpand?.sizeXXL || sizeXXL) : sizeXXL;
        if (windowWidth < 900) return sizeSM;
        if (windowWidth < 1024) return sizeMD;
        if (windowWidth < 1300) return effectiveSizeLG;
        if (windowWidth < 1600) return effectiveSizeXL;
        return effectiveSizeXXL;
    };

    const baseDrawerSize = getResponsiveSize(windowWidth);
    const shouldShrinkDrawerForMenu = Boolean(
        menuBar?.enabled &&
        !menuBarRailVisible &&
        windowWidth >= TW_XL_MIN,
    );
    const drawerSize = narrowDrawerSizeForHiddenMenu(baseDrawerSize, shouldShrinkDrawerForMenu);

    const menuBarButtons = menuBar?.enabled
        ? (menuBar?.buttons || []).map((button) => (
            <Tooltip
                key={button.id}
                title={button.title || button.id}
                placement={menuBar?.tooltipPlacement ?? (direction === 'right' ? 'left' : 'right')}
                zIndex={zIndex + 500}
            >
                <button
                    type='button'
                    onClick={() => menuBar?.onSelect?.(button.id)}
                    className={`group relative w-full inline-flex justify-center rounded-md p-2 transition-[transform,colors,box-shadow] duration-200 ease-out motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1 ${menuBar?.activeId === button.id
                        ? 'bg-slate-200 to-violet-600/50 text-indigo-700 shadow-[inset_0_1px_3px_rgba(15,23,42,0.1),inset_0_-1px_0_rgba(255,255,255,0.9)]'
                        : 'text-slate-500 shadow-[inset_0_1px_2px_rgba(15,23,42,0.06),inset_0_-1px_0_rgba(255,255,255,0.85)] hover:-translate-y-0.5 hover:bg-slate-50 hover:text-indigo-600 hover:shadow-[inset_0_1px_2px_rgba(15,23,42,0.05),inset_0_-1px_0_rgba(255,255,255,0.92)]'
                        }`}
                >
                    <span
                        className={`inline-flex transition-transform duration-200 ease-out motion-reduce:transition-none group-hover:scale-110 ${menuBar?.activeId === button.id
                            ? '[filter:drop-shadow(0_-1px_1px_rgba(49,46,129,0.38))_drop-shadow(0_1px_0_rgba(255,255,255,0.75))]'
                            : '[filter:drop-shadow(0_-1px_1px_rgba(15,23,42,0.32))_drop-shadow(0_1px_0_rgba(255,255,255,0.9))]'
                            }`}
                    >
                        {button.icon}
                    </span>
                </button>
            </Tooltip>
        ))
        : null;

    /** Columna del menú: en `xl+` pasa de `4rem` a `0` al ocultar (sin franja); el `size` del Drawer también se reduce. */
    const menuGutterRight = menuBar?.enabled && direction === 'right' ? (
        <div
            className={`relative max-xl:hidden xl:block min-h-0 shrink-0 self-stretch overflow-hidden bg-white ${menuGutterWidthMotion} ${menuBarRailVisible ? 'xl:w-16 xl:border-r xl:border-slate-200/90' : 'xl:w-0 xl:border-r-0'}`}
        >
            <aside
                className={`absolute inset-y-0 left-0 z-10 flex w-16 flex-col overflow-hidden bg-white ${menuBarRailVisible ? '' : 'pointer-events-none'} ${menuBar?.containerClassName || ''}`}
            >
                <div className="flex min-h-0 w-16 flex-1 flex-col items-center gap-2 p-2">
                    {menuBarButtons}
                </div>
            </aside>
        </div>
    ) : null;

    const menuGutterLeft = menuBar?.enabled && direction === 'left' ? (
        <div
            className={`relative max-xl:hidden xl:block min-h-0 shrink-0 self-stretch overflow-hidden bg-white ${menuGutterWidthMotion} ${menuBarRailVisible ? 'xl:w-16 xl:border-l xl:border-slate-200/90' : 'xl:w-0 xl:border-l-0'}`}
        >
            <aside
                className={`absolute inset-y-0 right-0 z-10 flex w-16 flex-col overflow-hidden bg-white ${menuBarRailVisible ? '' : 'pointer-events-none'} ${menuBar?.containerClassName || ''}`}
            >
                <div className="flex min-h-0 w-16 flex-1 flex-col items-center gap-2 p-2">
                    {menuBarButtons}
                </div>
            </aside>
        </div>
    ) : null;

    const resolvedExternalSide = !menuBar?.enabled ? externalSideContent : null;

    const drawerBody = (
        <section className="flex h-full flex-col">
            <main className="flex-1 overflow-auto overscroll-contain">
                {children}
            </main>
            <footer className="border-t border-gray-200 px-4 py-3">
                {footer}
            </footer>
        </section>
    );

    const drawerMotionStyle =
        menuBar?.enabled && windowWidth >= TW_XL_MIN
            ? {
                transitionProperty: 'transform, width',
                transitionDuration: `${MENU_RAIL_ANIM_MS}ms`,
                transitionTimingFunction: MENU_RAIL_ANIM_EASE,
            }
            : null;

    const useCustomPanelShell = Boolean(panelClassName) && !useUnifiedShell;

    return createPortal(
        <Drawer
            open={isOpen}
            onClose={handleClose}
            direction={direction}
            size={drawerSize}
            duration={menuBar?.enabled ? MENU_RAIL_ANIM_MS : 500}
            overlayOpacity={0.4}
            zIndex={zIndex}
            className={useCustomPanelShell ? 'cfamex-drawer-shell' : ''}
            style={{
                backgroundColor: 'transparent',
                height: '100dvh',
                boxShadow: 'none',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginLeft: `${direction === 'left' && windowWidth > 900 ? '15px' : '0px'}`,
                marginRight: `${direction === 'right' && windowWidth > 900 ? '15px' : '0px'}`,
                ...drawerMotionStyle,
            }}
        >
            <div
                className={`flex min-h-0 items-stretch ${windowWidth > 900 ? 'w-[100%]' : 'w-[96%]'} ${
                    useUnifiedShell
                        ? 'h-[96dvh] gap-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg shadow-slate-900/12'
                        : useCustomPanelShell
                            ? `h-[96dvh] overflow-hidden bg-white ${panelClassName}`
                            : `h-[96dvh] ${externalSideJoined ? 'gap-0' : 'gap-2'}`
                }`}
            >
                {direction === 'right' && resolvedExternalSide}
                <div
                    className={`relative flex min-h-0 min-w-0 flex-1 flex-col bg-white ${
                        useUnifiedShell
                            ? 'rounded-none shadow-none'
                            : useCustomPanelShell
                                ? 'min-h-0'
                                : 'rounded-md shadow-md'
                    }`}
                    style={{ height: useCustomPanelShell ? '100%' : '96dvh' }}
                >
                    {menuBar?.enabled ? (
                        <div className="flex min-h-0 min-w-0 flex-1 flex-col xl:flex-row xl:items-stretch">
                            {direction === 'right' && menuGutterRight}
                            <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
                                {drawerBody}
                            </div>
                            {direction === 'left' && menuGutterLeft}
                        </div>
                    ) : (
                        drawerBody
                    )}
                </div>
                {direction === 'left' && resolvedExternalSide}
            </div>
        </Drawer>,
        document.body,
    )
}
